import { useId, useState, type InputHTMLAttributes, type SubmitEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import { api, errorMessage, passwordProblem } from '../api';
import { AuthError } from './AuthShell';

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: string };

/** A password field with a show / hide toggle. */
export function PasswordInput({ label, className, ...input }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const id = useId();
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          {...input}
          id={id}
          type={visible ? 'text' : 'password'}
          className={cn('pr-17', className)}
        />
        <button
          type="button"
          className="absolute top-1/2 right-1.5 -translate-y-1/2 rounded-md px-2.5 py-1.5 text-sm font-semibold text-primary hover:bg-muted focus-visible:outline-3 focus-visible:outline-offset-1 focus-visible:outline-primary"
          aria-label={visible ? `Ẩn ${label.toLowerCase()}` : `Hiện ${label.toLowerCase()}`}
          aria-pressed={visible}
          aria-controls={id}
          onClick={() => setVisible((shown) => !shown)}
        >
          {visible ? 'Ẩn' : 'Hiện'}
        </button>
      </div>
    </div>
  );
}

interface PasswordFormProps {
  submitLabel: string;
  /** False for the forced change after sign-in with a temporary password. */
  requireCurrent: boolean;
  onChanged: () => void;
}

/** A new password, typed twice (checked against the hub's rule before sending). */
export function PasswordForm({ submitLabel, requireCurrent, onChanged }: PasswordFormProps) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const problem =
      passwordProblem(next) ?? (next !== repeat ? 'Hai lần nhập mật khẩu mới chưa khớp.' : null);
    if (problem) {
      setError(problem);
      return;
    }
    setPending(true);
    setError(null);
    try {
      await api.changePassword(requireCurrent ? current : undefined, next);
      setCurrent('');
      setNext('');
      setRepeat('');
      onChanged();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="grid gap-4" onSubmit={submit} noValidate>
      {requireCurrent && (
        <PasswordInput
          label="Mật khẩu hiện tại"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          required
        />
      )}
      <div className="grid gap-1.5">
        <PasswordInput
          label="Mật khẩu mới"
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          aria-describedby="password-rule"
          required
          autoFocus={!requireCurrent}
        />
        <small className="text-xs text-muted-foreground" id="password-rule">
          Ít nhất 8 ký tự, có chữ hoa, chữ thường và chữ số.
        </small>
      </div>
      <PasswordInput
        label="Nhập lại mật khẩu mới"
        autoComplete="new-password"
        value={repeat}
        onChange={(event) => setRepeat(event.target.value)}
        required
      />
      {error && <AuthError>{error}</AuthError>}
      <Button
        type="submit"
        size="lg"
        className="h-11"
        disabled={pending || (requireCurrent && !current) || !next || !repeat}
      >
        {pending ? 'Đang lưu…' : submitLabel}
      </Button>
    </form>
  );
}
